/**
 * Documents persistence adapter (records backend).
 *
 * Modeled on task-manager's boardsStore, but records are per-node (not a
 * whole-scope sync) so a rename or move touches one row. Three record types
 * in extension_documents (extension_id 'documents'):
 *
 * - 'folder'   : tree node. data { name }. parent_id + sort_key = tree place.
 * - 'document' : tree node. data { title }. parent_id + sort_key = tree place.
 *                Deliberately holds NO body, so listing the whole tree stays
 *                light (no rich-text blobs transferred to build the sidebar).
 * - 'docbody'  : one per document, id `${docId}::body`, scope_id = docId.
 *                data { scope_id, bodyHtml, bodyBlocks }. The heavy blob,
 *                loaded only when a document is opened — and its own
 *                field-level LWW unit, so editing a body never races the tree
 *                metadata. The editor's { version:1, blocks:[{id,node}] }
 *                Lexical snapshot (source of truth for collaborative
 *                editing) is stored COMPACT as `bodyBlocks` (docBodyCodec.js,
 *                since 14.10.2026 — about a fifth of the size); rows written
 *                by older versions carry it verbatim as `bodyLexical`. Either
 *                may be absent on legacy rows / brand-new docs that haven't
 *                been opened in the Lexical editor yet; bodyHtml is always
 *                dual-written alongside it for card previews, export, the AI
 *                bridge, and old clients. This module is the ONLY place that
 *                converts: every caller reads and writes the expanded
 *                snapshot as `bodyLexical`.
 *
 * Everything rides the normal local → SQLite → cloud/team sync path for free
 * (see records.rs / capabilities/records.js). Team sharing is automatic: the
 * records live in the workspace's synced store, so sharing the workspace with
 * a team shares its documents.
 *
 * Scope: every public function below takes a trailing `scope` descriptor —
 * { type: 'personal' } (default) or { type: 'team', teamId }. Personal is the
 * historical behavior (records in personalScopeId()'s fixed local bucket,
 * synced cross-device); team routes into the same `team:<teamId>` bucket
 * task-manager boards already use (teamBoardsSync registers one realtime
 * engine task per team that covers ALL extension_documents rows in that
 * scope — folders/documents/docbodies ride the same task/channel boards do,
 * no separate registration needed here). Every call site that predates this
 * scope param omits it and gets { type: 'personal' } — byte-identical to
 * before.
 *
 * Personal may also name a user-defined workspace: { type: 'personal',
 * scopeId } (card 5.1). Same bucket; only ROOT rows carry `scopeId`, and
 * `listTree` filters by it — see documentsScopeTree.js for the descriptor rule
 * (`scopeId` absent = whole bucket, `null` = Personal only).
 */
import { deleteRecord, listRecords, upsertRecord, writeRecords } from '../../../records/recordsClient';
import {
  announceDeviceItem,
  deviceScopeId,
  isDeviceScope,
  scopeForNewItem,
} from '../../../records/itemLocation';
import { personalScopeId, signedInAuthor } from '../../../services/personalScope';
import { teamScopeId } from '../task-manager/boardsStore';
import { markDocsDeleted, unmarkDocsDeleted, isDocDeleted } from './deletedDocsGuard';
import { applyScopeFilter, movedData, rootScopeStamp, scopeFilterOf } from './documentsScopeTree';
import { docBodyData, expandBlockNode, snapshotFromBodyData } from './docBodyCodec';
import { headData, isWholeBody, wholeSnapshot } from './docBodyHead';
import { assembleBody, documentBodyRev, isOwnBlockEcho, noteOwnBlockWrite, snapshotFromHtml } from './docBodyRead';
import { BLOCK_RECORD_TYPE, adoptStoredIds, blocksFromRows, planBlockWrites } from './docBlocks';

export { isWholeBody, documentBodyRev, isOwnBlockEcho, snapshotFromHtml };

export { docBodyData };

const EXTENSION_ID = 'documents';
const BODY_SUFFIX = '::body';

export function uid() {
  return (crypto.randomUUID && crypto.randomUUID()) || `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const PERSONAL_SCOPE = { type: 'personal' };

/**
 * Resolve a scope descriptor -> the extension_documents bucket it reads/
 * writes. { type: 'team', teamId } maps to the exact same teamScopeId(teamId)
 * helper task-manager boards use (imported, not duplicated) so a team's
 * documents and boards land in the identical `team:<teamId>` bucket. Anything
 * else (including no scope at all) is personal — the historical, per-device-
 * synced default. Always returns a scope (local-first) — never null.
 */
export function recordsContext(scope) {
  if (scope?.type === 'team' && scope.teamId != null) {
    return { workspaceId: teamScopeId(scope.teamId) };
  }
  // Free plan, cloud/local items: documents kept on this device only.
  if (scope?.type === 'device') {
    const device = deviceScopeId();
    if (device) return { workspaceId: device };
  }
  return { workspaceId: personalScopeId() };
}

// Moving a document between the cloud and this device is NOT here. It is the
// user's action only, and this store is the MCP documents surface: see
// records/itemMove.js (moveDocumentToDevice / moveDocumentToCloud).

/**
 * Where a NEW document goes (card C5). A ROOT personal document lands on this
 * device once the plan's cloud documents are full — never refused, so the
 * offline-first desktop app keeps working at the limit. A subpage always
 * follows its parent, or a tree would end up split across two scopes.
 */
function newDocumentScope(scope, parentId) {
  const personal = !scope || scope.type === 'personal';
  if (!personal || parentId) return scope;
  // The device scope has no sub-workspaces: a root that spills onto this
  // device leaves its workspace behind (rootScopeStamp sees type 'device').
  return scopeForNewItem('documents') ? { type: 'device' } : scope;
}

// ── sort keys ────────────────────────────────────────────────────────────────
// Rust lists records ORDER BY sort_key (a string), then id. We use fixed-width
// zero-padded integers so lexicographic order matches numeric order, with wide
// gaps so inserts between neighbors rarely need a sibling reindex.

const KEY_WIDTH = 15;
const KEY_STEP = 1_000_000;

export function padKey(n) {
  return String(Math.max(0, Math.floor(n))).padStart(KEY_WIDTH, '0');
}

/** A sort key strictly between prev/next (either may be null for the ends). */
export function keyBetween(prevKey, nextKey) {
  const p = prevKey != null ? Number(prevKey) : null;
  const n = nextKey != null ? Number(nextKey) : null;
  if (p != null && n != null) return padKey(Math.floor((p + n) / 2));
  if (p != null) return padKey(p + KEY_STEP);
  if (n != null) return padKey(Math.floor(n / 2));
  return padKey(KEY_STEP);
}

/** True when prev/next have collided (no integer between them) — caller reindexes. */
export function keysExhausted(prevKey, nextKey) {
  if (prevKey == null || nextKey == null) return false;
  return Number(nextKey) - Number(prevKey) <= 1;
}

// ── low-level record ops ──────────────────────────────────────────────────────

async function listByType(ctx, recordType) {
  return listRecords({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordType,
    scopeId: null,
  });
}

/**
 * The envelope keys Rust's `record_json` (sync/records.rs:35-49) spreads over a
 * record's `data` on the way out. Everything else on a returned row came from
 * `data` and is therefore the caller's own content.
 */
const ENVELOPE_KEYS = ['id', 'record_type', 'parent_id', 'sort_key'];

/**
 * The `data` keys of `nodeId`'s own tree record, MINUS the ones this module
 * writes itself (`name` / `title`, supplied fresh by the caller).
 *
 * `extension_records_upsert` replaces `data` wholesale — it never merges — so a
 * position-or-name-only write that builds `data` from scratch destroys every
 * other key on the row. That was invisible while `{name}`/`{title}` was all a
 * tree record ever held; it stopped being invisible when the Agent Notes folder
 * started carrying `mcpMemory` (documentsMemory.js), which a plain sidebar
 * rename or drag then silently erased, detaching the whole memory surface.
 * Reading the row back and carrying the unknown keys forward fixes that class
 * rather than that one key.
 *
 * A read that FAILS is not swallowed. "Row absent" (`{}`) and "could not read"
 * are different answers and only the first is safe: carrying `{}` forward after
 * a failed read is exactly the bug above, silently, with the rename still
 * reported as success. So a throwing read propagates and the rename fails
 * loudly — a visible failure beats a silent detach. (`saveDocumentBody`'s
 * best-effort shim is NOT a precedent for swallowing here: it guards editor
 * content the user can retype, not a load-bearing identity key that nothing
 * downstream can notice is gone.)
 *
 * `rows`, when given, is a pre-read `${record_type}:${id}` -> row lookup from
 * `readTreeRows` — one read for a whole resequencing pass instead of one per
 * node (see `readTreeRows`).
 */
function keepFrom(row) {
  const keep = {};
  for (const [key, value] of Object.entries(row)) {
    if (ENVELOPE_KEYS.includes(key) || key === 'name' || key === 'title') continue;
    keep[key] = value;
  }
  return keep;
}

async function preservedData(ctx, nodeId, recordType, rows = null) {
  if (rows) {
    const cached = rows.get(`${recordType}:${nodeId}`);
    return cached ? keepFrom(cached) : {};
  }
  const all = (await listByType(ctx, recordType)) || [];
  const row = all.find((r) => r.id === nodeId);
  if (!row) return {};
  return keepFrom(row);
}

/**
 * Reads every tree row (both record types) once and returns a
 * `${record_type}:${id}` -> row lookup to hand to `moveNode`.
 *
 * A sidebar drop resequences the whole destination sibling group, so
 * `DocumentsSidebar.applyMove` calls `moveNode` once per sibling; without this
 * each of those would issue its own whole-tree `extension_records_list` on an
 * interactive path (20 siblings = 20 whole-tree reads). One snapshot taken
 * before the pass is enough: a move rewrites only `parent_id`/`sort_key`, never
 * the `data` keys this lookup exists to carry forward.
 *
 * Returns `null` if the read fails, which makes `moveNode` fall back to reading
 * per node — and therefore to failing loudly, per `preservedData`.
 */
export async function readTreeRows(scope = PERSONAL_SCOPE) {
  const ctx = recordsContext(scope);
  if (!ctx) return null;
  try {
    const [folders, documents] = await Promise.all([
      listByType(ctx, 'folder'),
      listByType(ctx, 'document'),
    ]);
    const map = new Map();
    for (const row of folders || []) map.set(`folder:${row.id}`, row);
    for (const row of documents || []) map.set(`document:${row.id}`, row);
    return map;
  } catch {
    return null;
  }
}

async function upsert(ctx, record) {
  return upsertRecord({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    record,
  });
}

async function remove(ctx, recordId) {
  return deleteRecord({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordId: String(recordId),
  });
}

// ── public API ────────────────────────────────────────────────────────────────

/** `{ tags }` for a document row that has some (documentsMeta.js normalizes on write). */
function tagsOf(row) {
  const tags = Array.isArray(row.tags) ? row.tags.filter((t) => typeof t === 'string' && t) : [];
  return tags.length ? { tags } : {};
}

/**
 * The whole folder/document tree as flat nodes (no bodies). Shape:
 *   { id, type: 'folder'|'document', name, parentId, sortKey, scopeKey }
 * The sidebar assembles the hierarchy from parentId. `scopeKey` tags which
 * bucket the node came from (moveNode's cross-scope guard checks it).
 */
export async function listTree(scope = PERSONAL_SCOPE) {
  const ctx = recordsContext(scope);
  if (!ctx) return [];
  const filter = scopeFilterOf(scope);
  const personal = !scope?.type || scope.type === 'personal';
  const [folders, documents] = await Promise.all([listByType(ctx, 'folder'), listByType(ctx, 'document')]);
  // Only the personal bucket holds workspaces, and the docscope read is only
  // paid once some root actually names one — a scope-less tree costs nothing.
  const named = personal && [...folders, ...documents].some((r) => r.scopeId);
  const known = new Set(named ? ((await listByType(ctx, 'docscope')) || []).map((r) => String(r.id)) : []);
  const nodes = [
    ...folders.map((r) => ({
      id: r.id,
      type: 'folder',
      name: r.name || '',
      parentId: r.parent_id ?? null,
      sortKey: r.sort_key || '',
      scopeKey: ctx.workspaceId,
      ownScopeId: personal ? r.scopeId : undefined,
    })),
    ...documents.map((r) => ({
      id: r.id,
      type: 'document',
      name: r.title || '',
      parentId: r.parent_id ?? null,
      sortKey: r.sort_key || '',
      scopeKey: ctx.workspaceId,
      ownScopeId: personal ? r.scopeId : undefined,
      // Tags (card 2.3) only when there are any, so a tag-less tree keeps its shape.
      ...tagsOf(r),
    })),
  ];
  // Each node is tagged with its root's workspace as `scopeId` (absent =
  // Personal); a workspace descriptor keeps only its own nodes.
  return applyScopeFilter(nodes, filter, known);
}

/** The `data` key that marks a folder as the MCP agent-notes folder (documentsMemory.js). */
export const MEMORY_FOLDER_FLAG = 'mcpMemory';

/**
 * Plans v2 local caps: the user's OWN documents (personal + this-device
 * scopes, every workspace). Folders hold no content and do not count; team
 * documents belong to the team.
 */
export async function countOwnDocuments() {
  let n = 0;
  for (const scope of ownDocumentScopes(PERSONAL_SCOPE)) {
    const ctx = recordsContext(scope);
    if (!ctx) continue;
    // eslint-disable-next-line no-await-in-loop
    const [docs, folders] = await Promise.all([listByType(ctx, 'document'), listByType(ctx, 'folder')]);
    // The MCP agent's notes ("Agent Notes", documentsMemory.js) are not the
    // user's documents: uncapped, and never eating into the user's own room.
    const memory = new Set(
      (folders || []).filter((f) => f?.[MEMORY_FOLDER_FLAG] === true).map((f) => String(f.id))
    );
    n += (docs || []).filter((d) => !memory.has(String(d?.parent_id ?? ''))).length;
  }
  return n;
}

/** Full content of one document: { id, title, bodyHtml, bodyLexical }. Loaded on open. */
/**
 * The user's OWN two document scopes (cloud/local items): a document can move
 * between the cloud (`personal`) and this device (`device`) while keeping its
 * id, so a link or an open tab may still name the scope it used to live in.
 * Team scopes are never searched across. The personal entry is bucket-level
 * (no `scopeId`), so it covers every user workspace: a document moved between
 * workspaces is still found by its old link or tab.
 */
export function ownDocumentScopes(scope) {
  if (scope?.type === 'team') return [scope];
  const device = deviceScopeId() ? [{ type: 'device' }] : [];
  return scope?.type === 'device' ? [{ type: 'device' }, PERSONAL_SCOPE] : [PERSONAL_SCOPE, ...device];
}

/**
 * Read a document where it actually lives: its given scope first, then the
 * user's other own scope. Resolves `{ doc, scope }` or `{ doc: null, scope }`.
 * A personal hit inside a workspace names it (`{ type: 'personal', scopeId }`)
 * so an open tab re-locates into the right workspace after a move.
 */
export async function locateDocument(docId, scope = PERSONAL_SCOPE) {
  for (const candidate of ownDocumentScopes(scope)) {
    // eslint-disable-next-line no-await-in-loop
    const doc = await readDocument(docId, candidate);
    if (!doc) continue;
    if (candidate.type !== 'personal') return { doc, scope: candidate };
    let scopeId = null;
    try {
      // eslint-disable-next-line no-await-in-loop
      scopeId = (await listTree(candidate)).find((n) => n.id === docId)?.scopeId || null;
    } catch {
      /* the document itself was read; its workspace is a nicety */
    }
    return { doc, scope: scopeId ? { ...candidate, scopeId } : candidate };
  }
  return { doc: null, scope };
}

export async function readDocument(docId, scope = PERSONAL_SCOPE) {
  const ctx = recordsContext(scope);
  if (!ctx) return null;
  const rows = await listRecords({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordType: null,
    scopeId: String(docId),
  });
  const doc = rows.find((r) => r.id === docId && r.record_type === 'document');
  if (!doc) return null;
  const body = rows.find((r) => r.record_type === 'docbody');
  const assembled = await assembleBody(rows, body);
  return {
    id: docId,
    title: doc.title || '',
    bodyHtml: assembled.bodyHtml,
    bodyLexical: assembled.bodyLexical,
    // False while the body has not arrived on this device (not synced yet,
    // or too large to sync): an empty body is then unknown, not empty.
    hasBody: (Boolean(body) || assembled.blockMode) && !assembled.missing,
    // Stored per block (docBlocks.js) rather than as one whole docbody.
    blockMode: assembled.blockMode,
  };
}


/** Creates a folder under parentId (null = root) with the given sort key. */
export async function createFolder({ parentId = null, name = 'New Folder', sortKey }, scope = PERSONAL_SCOPE) {
  const ctx = recordsContext(scope);
  if (!ctx) throw new Error('Documents are unavailable for this workspace.');
  const id = uid();
  await upsert(ctx, {
    id,
    record_type: 'folder',
    parent_id: parentId,
    sort_key: sortKey,
    data: { name, ...rootScopeStamp(scope, parentId) },
  });
  return { id, type: 'folder', name, parentId, sortKey, scopeKey: ctx.workspaceId };
}

/**
 * Who made a new document and when (documents redesign 2.3: the author line
 * under the title). Opaque data like the title; documents made before this
 * carry neither and the author line says less.
 */
function creationStamp() {
  // Guarded: many test doubles of personalScope predate this getter.
  const author = typeof signedInAuthor === 'function' ? signedInAuthor() : null;
  return { createdAt: Date.now(), ...(author ? { createdBy: author } : {}) };
}

/**
 * Creates an empty document (meta + empty body) under parentId. Returns its id.
 * `id` is optional: when a caller needs to know the id BEFORE the write lands
 * (e.g. inserting a link to a not-yet-created subpage), it may pre-generate one
 * with `uid()` and pass it in; every existing caller omits it and gets a fresh
 * uid exactly as before.
 */
export async function createDocument(
  { id = null, parentId = null, title = '', sortKey },
  scope = PERSONAL_SCOPE,
  { allowDeviceFallback = true, aiGenerated = false, aiMarks = null } = {}
) {
  const target = allowDeviceFallback ? newDocumentScope(scope, parentId) : scope;
  const ctx = recordsContext(target);
  if (!ctx) throw new Error('Documents are unavailable for this workspace.');
  const docId = id ?? uid();
  await upsert(ctx, {
    id: docId,
    record_type: 'document',
    parent_id: parentId,
    sort_key: sortKey,
    data: {
      title,
      ...rootScopeStamp(target, parentId),
      ...creationStamp(),
      // AI Act Art. 50(2): a machine-readable mark on a document the in-app
      // assistant created. Opaque data like `createdBy`, so every later write
      // (title, move, rename: `keepFrom`) carries it forward.
      ...(aiGenerated === true ? { ai_generated: true } : {}),
      // …and its provenance (provider, model or MCP client, content
      // signature: services/ai/aiAuthorship.js aiCreatedMarks).
      ...(aiMarks && typeof aiMarks === 'object' ? aiMarks : {}),
    },
  });
  await upsert(ctx, {
    id: `${docId}${BODY_SUFFIX}`,
    record_type: 'docbody',
    parent_id: docId,
    sort_key: padKey(0),
    data: { scope_id: docId, bodyHtml: '' },
  });
  // Announced only now: the document exists, so the explanation is about
  // something the user can see.
  if (isDeviceScope(ctx.workspaceId) && (!scope || scope.type !== 'device')) announceDeviceItem('documents');
  return { id: docId, type: 'document', name: title, parentId, sortKey, scopeKey: ctx.workspaceId };
}

/**
 * Renames a node in place. `node` carries the live parentId/sortKey/type from
 * the sidebar tree so the upsert doesn't clobber the node's position, and
 * `preservedData` carries every other `data` key forward so the rename does not
 * clobber the node's content either (see `preservedData`).
 */
export async function renameNode(node, name, scope = PERSONAL_SCOPE) {
  const ctx = recordsContext(scope);
  if (!ctx) return;
  const keep = await preservedData(ctx, node.id, node.type);
  await upsert(ctx, {
    id: node.id,
    record_type: node.type,
    parent_id: node.parentId ?? null,
    sort_key: node.sortKey || padKey(KEY_STEP),
    data: node.type === 'folder' ? { ...keep, name } : { ...keep, title: name },
  });
}

/**
 * Moves a node to a new parent/sort position. `node` carries the live
 * name/type so the position-only change preserves its data. Guarded against a
 * cross-scope reparent: if `node` was read from a different bucket than the
 * one `scope` resolves to (its `scopeKey` tag, set by listTree, disagrees),
 * this throws rather than silently writing the record into the wrong bucket —
 * moving a document between personal and team ownership is a distinct,
 * deliberate operation (mirrors the board share / make-private moves), not a
 * side effect of a plain tree reparent.
 *
 * `rows` is optional and additive: a `readTreeRows` snapshot to read `data`
 * from instead of issuing this call's own whole-tree read. Every existing
 * caller omits it and behaves exactly as before.
 *
 * Workspaces (card 5.1) share the personal bucket, so moving a root between
 * them is THIS call with a `{ type: 'personal', scopeId }` target: the root's
 * `scopeId` changes and its subtree follows. A nested move drops the field.
 */
export async function moveNode(node, newParentId, newSortKey, scope = PERSONAL_SCOPE, rows = null) {
  const ctx = recordsContext(scope);
  if (!ctx) return;
  if (node?.scopeKey != null && node.scopeKey !== ctx.workspaceId) {
    throw new Error(`Cannot move document "${node.id}" across scopes (${node.scopeKey} -> ${ctx.workspaceId}).`);
  }
  const keep = await preservedData(ctx, node.id, node.type, rows);
  await upsert(ctx, {
    id: node.id,
    record_type: node.type,
    parent_id: newParentId ?? null,
    sort_key: newSortKey,
    data: movedData(
      node.type === 'folder' ? { ...keep, name: node.name } : { ...keep, title: node.name },
      newParentId,
      scope
    ),
  });
}

/** Saves a document's title (fetches live position to avoid clobbering it). */
export async function saveDocumentTitle(docId, title, scope = PERSONAL_SCOPE) {
  // A locally deleted document must never be written again — a late title
  // flush would resurrect the tombstoned row (see deletedDocsGuard.js).
  if (isDocDeleted(docId)) return;
  const ctx = recordsContext(scope);
  if (!ctx) return;
  const rows = await listRecords({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordType: null,
    scopeId: String(docId),
  });
  const doc = rows.find((r) => r.id === docId && r.record_type === 'document');
  if (!doc) return;
  await upsert(ctx, {
    id: docId,
    record_type: 'document',
    parent_id: doc.parent_id ?? null,
    sort_key: doc.sort_key || padKey(KEY_STEP),
    // Carry the row's other keys (a root's `scopeId`, …): `data` is replaced
    // wholesale, so a title-only write would drop the page out of its workspace.
    data: { ...keepFrom(doc), title },
  });
}

/**
 * AI Act Art. 50(2): stamps the AI-edit marks (`ai_edited_at`, provider /
 * model or MCP client, content signature — services/ai/aiAuthorship.js) on an
 * existing document's meta record after an AI wrote its body. Every other key
 * is carried forward (`keepFrom`), so `createdBy` / `createdAt` — the human
 * authorship — and an earlier `ai_generated` stay exactly as they were. The
 * body record is not touched, so its `rev` (MCP's `expected_rev`) is unchanged.
 */
export async function markDocumentAi(docId, marks, scope = PERSONAL_SCOPE) {
  if (isDocDeleted(docId) || !marks || typeof marks !== 'object') return;
  const ctx = recordsContext(scope);
  if (!ctx) return;
  const rows = await listRecords({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordType: null,
    scopeId: String(docId),
  });
  const doc = (rows || []).find((r) => r.id === docId && r.record_type === 'document');
  if (!doc) return;
  await upsert(ctx, {
    id: docId,
    record_type: 'document',
    parent_id: doc.parent_id ?? null,
    sort_key: doc.sort_key || padKey(KEY_STEP),
    data: { ...keepFrom(doc), title: doc.title ?? '', ...marks },
  });
}

/**
 * Saves a document's body as block records (docBlocks.js): only the blocks
 * whose content or position changed are written, removed ones are deleted —
 * an autosave of a long page sends a paragraph, not the page.
 *
 * Shapes: `saveDocumentBody(docId, { bodyHtml, bodyLexical }, scope)` (the
 * editor: its snapshot is the body), or `saveDocumentBody(docId, html,
 * scope)` / `{ bodyHtml, bodyLexical: null }` — a writer that has only HTML
 * (AI, MCP, git) REPLACES the body with it, turned into blocks with the ids an
 * editor opening that HTML would give them.
 *
 * The `docbody` row stays, as the body's head: the first save of a body
 * still stored whole moves it to blocks (and clears the whole copy, so it
 * can never win over the blocks again — assembleBody), and its `bodyHtml` is
 * a snapshot for older app versions, rewritten only when `htmlSnapshot` is
 * true (every writer but the editor's autosave, which refreshes it when the
 * typing settles — useDocumentBody.js).
 */
export async function saveDocumentBody(
  docId,
  bodyOrOptions,
  scope = PERSONAL_SCOPE,
  { htmlSnapshot = true, onlyIds = null, movedIds = null, fromHtml = false, adoptIds = true, expectedRev = null } = {}
) {
  // THE delete-vs-autosave guard: a tab's flush-on-unmount (or a pending
  // debounce timer, or the outbox's closed-doc rewrite) firing after
  // deleteNodes began would write the body AFTER its tombstone — orphaned,
  // invisible rows leaking against the sync row cap. deleteNodes marks the
  // id synchronously before its first write; anything arriving here
  // afterwards is a no-op (see deletedDocsGuard.js).
  if (isDocDeleted(docId)) return;
  const ctx = recordsContext(scope);
  if (!ctx) return;
  const bodyHtml = typeof bodyOrOptions === 'string' ? bodyOrOptions : bodyOrOptions?.bodyHtml || '';
  const given = typeof bodyOrOptions === 'string' ? null : bodyOrOptions?.bodyLexical ?? null;
  const derived = !(given && Array.isArray(given.blocks));
  let snapshot = derived ? await snapshotFromHtml(docId, bodyHtml) : given;
  if (!snapshot) {
    await saveWholeHtml(ctx, docId, bodyHtml);
    return;
  }
  let rows = await listRecords({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordType: null,
    scopeId: String(docId),
  });
  if (isDocDeleted(docId)) return;
  rows = rows || [];
  const body = rows.find((r) => r.record_type === 'docbody');
  const stored = blocksFromRows(rows);
  // MCP's expected_rev, checked against exactly the rows this write starts
  // from (the editor's autosave runs on this same thread: nothing lands
  // between here and the writes below being queued).
  if (expectedRev != null) {
    const whole = !stored.recordIds.size || isWholeBody(body);
    const actual = await documentBodyRev(
      whole ? { bodyLexical: wholeSnapshot(body), bodyHtml: (body && body.bodyHtml) || '' } : { bodyLexical: stored.snapshot }
    );
    // eslint-disable-next-line no-throw-literal
    if (actual !== expectedRev) throw `conflict:${actual}`;
  }
  // (Not when an open editor is about to show this HTML itself — `adoptIds`
  // false: it derives the seeded ids, and the store must hold the same ones.)
  if ((derived || fromHtml) && adoptIds) snapshot = adoptStoredIds(snapshot, stored);
  // Moving a body to blocks (or taking back an older app's whole save) writes
  // every block: there is nothing stored per block to keep yet.
  const moving = !body || isWholeBody(body) || !stored.recordIds.size;
  // Blocks deleted at this screen stay deleted even when moving (which writes
  // every block but can only delete records this device holds).
  const onScreen = new Set(snapshot.blocks.map((b) => b.id));
  const deletedHere = onlyIds ? [...onlyIds].filter((id) => !onScreen.has(id)) : null;
  const { upserts, deletes } = planBlockWrites(docId, stored, snapshot, { onlyIds: moving ? null : onlyIds, deletedHere, movedIds });
  upserts.filter((r) => !deletes.includes(r.id)).forEach(noteOwnBlockWrite);
  await writeRecords({ workspaceId: ctx.workspaceId, extensionId: EXTENSION_ID, upserts, deletes });
  if (!moving && !htmlSnapshot) return { head: false };
  const head = headData(docId, bodyHtml, snapshot);
  const changed = !body || body.htmlSig !== head.htmlSig || body.snapSig !== head.snapSig || !!body.htmlOmitted !== head.htmlOmitted;
  if (!moving && !changed) return { head: false };
  await upsert(ctx, {
    id: `${docId}${BODY_SUFFIX}`,
    record_type: 'docbody',
    parent_id: docId,
    sort_key: padKey(0),
    data: head,
  });
  return { head: true };
}

/**
 * An HTML-only write with no HTML -> blocks conversion installed
 * (bodyHtmlCodec.js): the body goes back to being stored whole, as HTML —
 * its blocks are deleted so they cannot win over it (assembleBody). The next
 * editor save moves it to blocks again.
 */
async function saveWholeHtml(ctx, docId, bodyHtml) {
  await removeBlocks(ctx, docId);
  if (isDocDeleted(docId)) return;
  await upsert(ctx, {
    id: `${docId}${BODY_SUFFIX}`,
    record_type: 'docbody',
    parent_id: docId,
    sort_key: padKey(0),
    data: { scope_id: docId, bodyHtml: bodyHtml || '', blocks: false, bodyBlocks: null, bodyLexical: null },
  });
}

/**
 * Deletes a set of nodes (tombstones). Pass a folder plus all its descendants
 * (the sidebar computes the subtree). Documents also tombstone their body row.
 */
export async function deleteNodes(nodes, scope = PERSONAL_SCOPE) {
  const ctx = recordsContext(scope);
  if (!ctx) return;
  // SYNCHRONOUSLY, before the first await: any save that starts after this
  // call was invoked — the unmount flush of a tab this delete is about to
  // close, a still-armed debounce timer — sees the mark and no-ops instead
  // of resurrecting a tombstoned row (see deletedDocsGuard.js).
  markDocsDeleted(nodes.filter((n) => n.type === 'document').map((n) => n.id));
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    let rowGone = false;
    try {
      await remove(ctx, node.id);
      rowGone = true;
      if (node.type === 'document') {
        await remove(ctx, `${node.id}${BODY_SUFFIX}`);
        await removeBlocks(ctx, node.id);
      }
    } catch (err) {
      // A mid-loop failure leaves the REST of the subtree visible — those
      // docs must stay saveable, so their marks are lifted. Without this,
      // every future save of a still-alive doc silently no-oped while the
      // tab reported saved (review finding). The already-deleted prefix
      // keeps its marks — including this node once its own row is gone (its
      // body or block cleanup failed): a save then would resurrect a body
      // under a deleted page. The failure is rethrown for the caller.
      unmarkDocsDeleted(
        nodes.slice(rowGone ? i + 1 : i).filter((n) => n.type === 'document').map((n) => n.id)
      );
      throw err;
    }
  }
}


/** A document's stored blocks with their keys (docBlocks.blocksFromRows) — for the open editor's reconcile. */
export async function readDocumentBlocks(docId, scope = PERSONAL_SCOPE) {
  const ctx = recordsContext(scope);
  if (!ctx) return blocksFromRows([]);
  const rows = await listRecords({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordType: BLOCK_RECORD_TYPE,
    scopeId: String(docId),
  });
  return blocksFromRows(rows);
}

/** Tombstones every block record of a document (deleteNodes). */
async function removeBlocks(ctx, docId) {
  const rows = await listRecords({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordType: BLOCK_RECORD_TYPE,
    scopeId: String(docId),
  });
  const deletes = (rows || []).filter((row) => row.record_type === BLOCK_RECORD_TYPE).map((row) => row.id);
  await writeRecords({ workspaceId: ctx.workspaceId, extensionId: EXTENSION_ID, deletes });
}

/**
 * Wire `RemoteChange` (entity_type "extension_documents", as broadcast on the
 * `team:{teamId}` phoenix channel) -> the flat record shape realtime document
 * callers expect. Mirrors boardsStore.remoteChangeToRecord exactly (same wire
 * envelope for every extension_documents row, whichever extension_id owns
 * it): `data` carries extension_id/record_type/parent_uid/sort_key alongside
 * the record's own fields; `id` and `deleted` live on the change itself. A
 * delete's `data` is null on the wire (the server nils it out — see
 * change_entry/format_entity in backend_pheonix/sync.ex) so record_type (and
 * therefore folder/document/docbody) can't always be told apart for a
 * delete — callers that need to distinguish must look the id up locally.
 */
export function remoteChangeToDocRecord(change) {
  const { extension_id, record_type, parent_uid, sort_key, ...fields } = change?.data || {};
  // A docbody's snapshot is handed on EXPANDED as `bodyLexical`, like
  // readDocument's, so the editor's own-echo check compares like with like.
  if (record_type === 'docbody' && fields.bodyBlocks) {
    fields.bodyLexical = snapshotFromBodyData(fields);
    delete fields.bodyBlocks;
  }
  // A block arrives as the editor holds it (docBlocks.js stores it compact).
  if (record_type === BLOCK_RECORD_TYPE && fields.node) fields.node = expandBlockNode(fields.node);
  return {
    id: change.entity_id,
    record_type,
    parent_id: parent_uid ?? null,
    sort_key,
    deleted: !!change.deleted,
    ...fields,
  };
}
